import { Divider, Flex, Heading } from '@hover/blueprint';
import React, { useMemo, useState } from 'react';

import { EmptyDesign } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/CurrentDesignMenu/EmptyDesign';
import { VariationSearchItem } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/ProductSearch/SearchByName/VariationSearchItem';
import { VariationDetails } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/ProductSearch/VariationDetails';
import { useRoofMaterialGroups } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/RoofMenu/useRoofMaterialGroups';
import { useTrimMaterialGroups } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/TrimMenu/useTrimMaterialGroups';
import { useWallMaterialGroups } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/WallMenu/useWallMaterialGroups';
import type { Variation } from 'src/modules/designStudio/helpers/productSelection/types';
import { ProductCategory } from 'src/modules/designStudio/helpers/productSelection/types';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

enum ActiveView {
  VIEW_MATERIALS = 'VIEW_MATERIALS',
  VIEW_VARIATION = 'VIEW_VARIATION',
}

export const CurrentDesignMenu = () => {
  const t = useTranslate(TranslationNamespace.DESIGN_STUDIO);

  const [activeCategory, setActiveCategory] = useState<
    ProductCategory | undefined
  >(undefined);

  const roofGroups = useRoofMaterialGroups();
  const wallGroups = useWallMaterialGroups();
  const trimGroups = useTrimMaterialGroups();

  const [activeView, setActiveView] = useState<ActiveView>(
    ActiveView.VIEW_MATERIALS,
  );
  const [selectedVariation, setSelectedVariation] = useState<
    Variation | undefined
  >(undefined);

  const roofVariations = useMemo(
    () =>
      roofGroups
        .filter(group => !!group.variation)
        .map(g => g.variation) as Variation[],
    [roofGroups],
  );
  const wallVariations = useMemo(
    () =>
      wallGroups
        .filter(group => !!group.variation)
        .map(g => g.variation) as Variation[],
    [wallGroups],
  );
  const trimVariations = useMemo(
    () =>
      trimGroups
        .filter(group => !!group.variation)
        .map(g => g.variation) as Variation[],
    [trimGroups],
  );

  const designIsEmpty = useMemo(
    () =>
      roofVariations.length === 0 &&
      wallVariations.length === 0 &&
      trimVariations.length === 0,
    [roofVariations, wallVariations, trimVariations],
  );

  const actionsByView: Record<
    ActiveView,
    Record<string, (...args: any) => void>
  > = {
    [ActiveView.VIEW_MATERIALS]: {
      onViewVariation: (variation: Variation) => {
        setSelectedVariation(variation);
        setActiveView(ActiveView.VIEW_VARIATION);
      },
    },
    [ActiveView.VIEW_VARIATION]: {
      onBack: () => {
        setActiveView(ActiveView.VIEW_MATERIALS);
        setSelectedVariation(undefined);
      },
    },
  };

  const viewByState: Record<ActiveView, React.ReactElement> = {
    [ActiveView.VIEW_MATERIALS]: (() => {
      const actions = actionsByView[ActiveView.VIEW_MATERIALS];

      if (designIsEmpty) {
        return <EmptyDesign />;
      }

      return (
        <Flex direction="column" gap={300}>
          <Heading size={500}>{t('menu.share.materials')}</Heading>
          {roofVariations.length !== 0 && (
            <>
              <Heading size={200}>{t('menu.roof.title')}</Heading>
              {roofVariations.map(variation => (
                <VariationSearchItem
                  key={variation.uuid}
                  onSelect={() => {
                    setActiveCategory(ProductCategory.ROOF);
                    actions.onViewVariation(variation);
                  }}
                  variation={variation}
                />
              ))}
              <Divider />
            </>
          )}
          {wallVariations.length !== 0 && (
            <>
              <Heading size={200}>{t('menu.wall.title')}</Heading>
              {wallVariations.map(variation => (
                <VariationSearchItem
                  key={variation.uuid}
                  onSelect={() => {
                    setActiveCategory(ProductCategory.WALLS);
                    actions.onViewVariation(variation);
                  }}
                  variation={variation}
                />
              ))}
              <Divider />
            </>
          )}
          {trimVariations.length !== 0 && (
            <>
              <Heading size={200}>{t('menu.trim.title')}</Heading>
              {[trimVariations[trimVariations.length - 1]].map(variation => (
                <VariationSearchItem
                  key={variation.uuid}
                  onSelect={() => {
                    setActiveCategory(ProductCategory.TRIM);
                    actions.onViewVariation(variation);
                  }}
                  variation={variation}
                />
              ))}
              <Divider />
            </>
          )}
        </Flex>
      );
    })(),
    [ActiveView.VIEW_VARIATION]: (() => {
      const actions = actionsByView[ActiveView.VIEW_VARIATION];
      return (
        <VariationDetails
          category={activeCategory ?? ProductCategory.WALLS}
          onBack={actions.onBack}
          variation={selectedVariation as Variation}
        />
      );
    })(),
  };

  return viewByState[activeView];
};
